import {
  AffiliateClientsResponse,
  AffiliateEarningsResponse,
  AffiliateMeResponse,
} from '@avanzu/types';
import { ContentCopy } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import PaperSection from '../../components/PaperSection';
import { apiClient } from '../../services/apiClient';

const formatCents = (cents: number, currency: string) =>
  `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;

interface StatCardProps {
  label: string;
  value: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
  </Paper>
);

const DashboardPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [me, setMe] = useState<AffiliateMeResponse['affiliate'] | null>(null);
  const [earnings, setEarnings] = useState<AffiliateEarningsResponse | null>(null);
  const [clients, setClients] = useState<AffiliateClientsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [meRes, earningsRes, clientsRes] = await Promise.all([
          apiClient.get('/affiliate/me'),
          apiClient.get('/affiliate/earnings'),
          apiClient.get('/affiliate/clients'),
        ]);
        if (meRes.ok) setMe(((await meRes.json()) as AffiliateMeResponse).affiliate);
        if (earningsRes.ok) setEarnings(await earningsRes.json());
        if (clientsRes.ok) setClients(await clientsRes.json());
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const copyLink = () => {
    if (!me) return;
    navigator.clipboard.writeText(me.referralLink);
    enqueueSnackbar('Referral link copied', { variant: 'success' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const currency = earnings?.totals.currency ?? 'aud';
  const paidClients = clients?.clients.filter((c) => c.hasPaidProject).length ?? 0;

  return (
    <Stack spacing={3}>
      <PaperSection title="Overview">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Referred clients" value={String(clients?.total ?? 0)} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="With paid project" value={String(paidClients)} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Pending commission"
              value={formatCents(earnings?.totals.pendingAmount ?? 0, currency)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Paid to date"
              value={formatCents(earnings?.totals.paidAmount ?? 0, currency)}
            />
          </Grid>
        </Grid>
      </PaperSection>

      {me && (
        <PaperSection title="Your referral link">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'monospace',
                bgcolor: 'action.hover',
                px: 2,
                py: 1,
                borderRadius: 1,
                overflowX: 'auto',
              }}
            >
              {me.referralLink}
            </Typography>
            <Tooltip title="Copy link">
              <IconButton onClick={copyLink}>
                <ContentCopy />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You earn {(me.commissionRateBps / 100).toFixed(0)}% of a referred organisation's first
            paid project; they get {(me.discountRateBps / 100).toFixed(0)}% off it.
          </Typography>
        </PaperSection>
      )}
    </Stack>
  );
};

export default DashboardPage;
